<template>
  <div v-if="hasAccess" class="ai-assistant-page">
    <page-content class="ai-assistant-page__content">
      <section class="ai-panel">
        <header class="ai-toolbar">
          <label v-if="activeTab === 'knowledge'" class="ai-search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              v-model.trim="search"
              type="search"
              placeholder="Rechercher un accord"
              aria-label="Rechercher un accord par titre, type ou statut"
            >
          </label>
          <dashboard-date-filters
            v-else
            class="ai-period-filters"
            :quick-ranges="quickRanges"
            :active-range-id="activeQuickRangeId"
            :date-range="dateRange"
            :max-date="maxDate"
            @select-range="applyQuickRange"
            @show-full="showFull"
            @update:date-range="handleDateRangeUpdate"
          />

          <nav class="ai-tabs" aria-label="Sections de l’assistant IA">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              type="button"
              class="ai-tabs__item"
              :class="{ 'ai-tabs__item--active': activeTab === tab.key }"
              @click="selectTab(tab.key)"
            >
              {{ tab.label }}
            </button>
          </nav>
        </header>

        <ai-tracking v-if="activeTab === 'tracking'" :date-range="dateRange" />
        <ai-knowledge v-else :search="search" />
      </section>
    </page-content>
  </div>

  <div v-else class="px-6 py-10">
    <p class="text-sm text-gray-500">Vous n’avez pas accès à cette section.</p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { format, subDays, subMonths, subWeeks } from 'date-fns'
import PageContent from '@/components/PageContent.vue'
import DashboardDateFilters from '@/components/dashboard/DashboardDateFilters.vue'
import { DASHBOARD_QUICK_RANGES } from '@/pages/dashboard/state'
import { getDomainPeriodStartDate } from '@/pages/dashboard/domainPeriod'
import { getTrackingQueryState, syncTrackingQueryFromVm } from '@/pages/dashboard/trackingQuery'
import AiTracking from './Tracking.vue'
import AiKnowledge from './Knowledge.vue'

/**
 * "Assistant IA" tab of the domain menu, for directors and operators.
 *
 * Everything shown here is scoped to the active domain by the API (the
 * `Domain` header); the internal cross-domain screens live under
 * /app/dashboard/tracking/ai and stay reserved to admins / CMs.
 *
 * The period lives here rather than in the tracking tab so that it survives a
 * switch to the knowledge tab, and is persisted in the URL with the same keys
 * as the tracking pages. Its selector only shows on the tracking tab.
 */
const ALLOWED_ROLES = ['admin', 'community-manager', 'director', 'operator']

const TABS = [
  { key: 'tracking', label: 'Suivi' },
  { key: 'knowledge', label: 'Base de connaissances' }
]

const DEFAULT_RANGE_ID = 'month-1'

const toIsoDate = date => format(date, 'yyyy-MM-dd')

export default {
  name: 'app-ai-assistant-home',
  components: { PageContent, DashboardDateFilters, AiTracking, AiKnowledge },
  data () {
    const today = new Date()
    const quickRanges = DASHBOARD_QUICK_RANGES.map(range => ({ ...range }))
    const trackingState = getTrackingQueryState(this.$route.query, {
      activeQuickRangeId: DEFAULT_RANGE_ID,
      dateRange: null
    })
    const domain = this.$store.getters.domain

    // A quick range coming from the URL wins over stored dates so that the
    // period is recomputed from today, like on the dashboard.
    let dateRange = trackingState.dateRange
    if (trackingState.activeQuickRangeId === 'full') {
      dateRange = fullRange(domain, today)
    } else {
      const range = quickRanges.find(item => item.id === trackingState.activeQuickRangeId)
      if (range) {
        dateRange = rangeFor(range, today)
      }
    }
    if (!Array.isArray(dateRange) || dateRange.length !== 2) {
      dateRange = rangeFor(quickRanges.find(item => item.id === DEFAULT_RANGE_ID), today)
    }

    const requestedTab = this.$route.query.tab
    return {
      tabs: TABS,
      activeTab: TABS.some(tab => tab.key === requestedTab) ? requestedTab : 'tracking',
      search: '',
      quickRanges,
      activeQuickRangeId: trackingState.activeQuickRangeId,
      dateRange,
      // Read by syncTrackingQueryFromVm; this page has no domain picker.
      selectedDomainUuids: [],
      maxDate: toIsoDate(today)
    }
  },
  computed: {
    ...mapGetters(['roles', 'domain']),
    hasAccess () {
      return this.roles.some(role => ALLOWED_ROLES.includes(role))
    }
  },
  watch: {
    dateRange: {
      immediate: true,
      handler () {
        syncTrackingQueryFromVm(this)
      }
    },
    'domain.uuid' () {
      if (this.activeQuickRangeId === 'full') {
        this.dateRange = fullRange(this.domain, new Date())
      }
    }
  },
  methods: {
    selectTab (tab) {
      if (tab === this.activeTab) {
        return
      }
      this.activeTab = tab
      this.search = ''
      const query = { ...(this.$route.query || {}) }
      if (tab === 'tracking') {
        delete query.tab
      } else {
        query.tab = tab
      }
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    applyQuickRange (range) {
      const today = new Date()
      this.maxDate = toIsoDate(today)
      this.activeQuickRangeId = range.id
      this.dateRange = rangeFor(range, today)
    },
    showFull () {
      const today = new Date()
      this.maxDate = toIsoDate(today)
      this.activeQuickRangeId = 'full'
      this.dateRange = fullRange(this.domain, today)
    },
    handleDateRangeUpdate (range) {
      if (!Array.isArray(range) || range.length !== 2) {
        return
      }
      this.activeQuickRangeId = null
      this.dateRange = [...range]
    }
  }
}

function rangeFor (range, today) {
  let start
  switch (range && range.type) {
    case 'day':
      start = subDays(today, range.amount)
      break
    case 'month':
      start = subMonths(today, range.amount)
      break
    case 'week':
    default:
      start = subWeeks(today, range && range.amount ? range.amount : 1)
  }
  return [toIsoDate(start), toIsoDate(today)]
}

function fullRange (domain, today) {
  const end = toIsoDate(today)
  const start = getDomainPeriodStartDate(domain)
  return [start > end ? end : start, end]
}
</script>

<style scoped>
.ai-assistant-page {
  background: #f4f6f9;
  min-height: calc(100vh - 64px);
  min-width: 0;
  width: 100%;
}

.ai-assistant-page__content {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.ai-panel {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  max-width: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.ai-toolbar {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px 24px;
  padding: 22px 28px;
}

.ai-search {
  display: flex;
  flex: 1 1 260px;
  align-items: center;
  max-width: 360px;
  min-width: 240px;
  padding: 0 14px;
  border: 1px solid #d1d5db;
  border-radius: 9px;
}

.ai-search:focus-within {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.ai-search i {
  color: #9ca3af;
}

.ai-search input {
  width: 100%;
  min-width: 0;
  padding: 11px;
  border: 0;
  box-shadow: none;
  font-size: 14px;
  outline: none;
}

.ai-tabs {
  display: flex;
  margin-left: auto;
  padding: 4px;
  border-radius: 999px;
  background: #f3f4f6;
}

.ai-tabs__item {
  padding: 10px 22px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #4b5563;
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
}

.ai-tabs__item--active {
  background: #2563eb;
  box-shadow: 0 2px 5px rgba(37, 99, 235, 0.25);
  color: #ffffff;
}

@media (max-width: 1100px) {
  .ai-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .ai-search {
    flex: none;
    max-width: 100%;
    min-width: 0;
    width: 100%;
  }

  .ai-tabs {
    align-self: flex-start;
    margin-left: 0;
    max-width: 100%;
    overflow-x: auto;
  }
}
</style>
